export type EntityRelevanceLevel =
  | 'unknown'
  | 'minor'
  | 'supporting'
  | 'notable'
  | 'major'
  | 'central';

export const ENTITY_RELEVANCE_SEGMENT_COUNT = 5;

export const EDITABLE_ENTITY_RELEVANCE_LEVELS: Exclude<EntityRelevanceLevel, 'unknown'>[] = [
  'minor',
  'supporting',
  'notable',
  'major',
  'central',
];

export function clampEntityRelevance(value: unknown): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function getEntityRelevanceLevel(value: number | undefined): EntityRelevanceLevel {
  if (value == null || value <= 0) {
    return 'unknown';
  }
  if (value <= 20) {
    return 'minor';
  }
  if (value <= 40) {
    return 'supporting';
  }
  if (value <= 60) {
    return 'notable';
  }
  if (value <= 80) {
    return 'major';
  }
  return 'central';
}

export function getEntityRelevanceFilledSegments(value: number | undefined): number {
  const level = getEntityRelevanceLevel(value);
  switch (level) {
    case 'minor':
      return 1;
    case 'supporting':
      return 2;
    case 'notable':
      return 3;
    case 'major':
      return 4;
    case 'central':
      return 5;
    default:
      return 0;
  }
}

export function entityRelevanceLevelLabelKey(level: EntityRelevanceLevel): string {
  return `entity_relevance_${level}`;
}

export function relevanceScoreForLevel(level: EntityRelevanceLevel): number | undefined {
  switch (level) {
    case 'minor':
      return 10;
    case 'supporting':
      return 30;
    case 'notable':
      return 50;
    case 'major':
      return 70;
    case 'central':
      return 90;
    default:
      return undefined;
  }
}

export function defaultCreateEntityRelevance(lastCreatedRelevance?: number | null): number {
  return clampEntityRelevance(lastCreatedRelevance) ?? relevanceScoreForLevel('central')!;
}

const LAST_CREATED_CHARACTER_RELEVANCE_KEY_PREFIX = 'nb:last-created-character-relevance:';

function lastCreatedCharacterRelevanceKey(documentId: string): string {
  return `${LAST_CREATED_CHARACTER_RELEVANCE_KEY_PREFIX}${documentId}`;
}

export function readLastCreatedCharacterRelevance(documentId: string): number | undefined {
  try {
    const raw = localStorage.getItem(lastCreatedCharacterRelevanceKey(documentId));
    if (raw == null || raw === '') {
      return undefined;
    }
    return clampEntityRelevance(Number(raw));
  } catch {
    return undefined;
  }
}

export function writeLastCreatedCharacterRelevance(
  documentId: string,
  relevance: number | undefined
): void {
  try {
    const key = lastCreatedCharacterRelevanceKey(documentId);
    const clamped = clampEntityRelevance(relevance);
    if (clamped == null) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, String(clamped));
  } catch {
  }
}
